import { Loader2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
} from '@/components/ui/alert-dialog'

export default function PriceChangeConfirmDialog({
  pendingPriceCheck,
  onConfirm,
  onCancel
}) {
  if (!pendingPriceCheck) return null

  const { loading, priceComparison } = pendingPriceCheck

  // Loading state while recalculating
  if (loading) {
    return (
      <AlertDialog open onOpenChange={() => {}}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Checking Prices</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            <div className="flex items-center gap-3 py-4">
              <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
              <span>Recalculating prices against current catalogue...</span>
            </div>
          </AlertDialogDescription>
        </AlertDialogContent>
      </AlertDialog>
    )
  }

  // No price comparison data yet
  if (!priceComparison) return null

  const { items, oldTotal, newTotal, customerName } = priceComparison
  const totalDiff = newTotal - oldTotal

  return (
    <AlertDialog open onOpenChange={() => onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Prices Have Changed</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription>
          <span>
            The following prices for <strong className="text-neutral-700">{customerName}</strong> have
            changed since this order was created.
          </span>
        </AlertDialogDescription>

        <div className="w-full overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-neutral-500">
                <th className="pb-2 pr-3 font-medium">Product</th>
                <th className="pb-2 pr-3 font-medium text-right">Qty</th>
                <th className="pb-2 pr-3 font-medium text-right">Old /kg</th>
                <th className="pb-2 pr-3 font-medium text-right">New /kg</th>
                <th className="pb-2 font-medium text-right">Diff</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const diff = item.newSubtotal - item.oldSubtotal
                return (
                  <tr
                    key={item.saleItemId}
                    className={item.changed
                      ? 'text-neutral-900'
                      : 'text-neutral-400'
                    }
                  >
                    <td className="py-1.5 pr-3">
                      {item.strain}
                      <span className="text-neutral-400 ml-1">({item.productTypeName})</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{item.quantity}kg</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      £{(item.oldPrice ?? 0).toFixed(2)}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {item.changed ? (
                        <span className="font-medium">£{(item.newPrice ?? 0).toFixed(2)}</span>
                      ) : (
                        <span>£{(item.newPrice ?? 0).toFixed(2)}</span>
                      )}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {item.changed ? (
                        <span className={diff > 0 ? 'text-red-600' : 'text-green-600'}>
                          {diff > 0 ? '+' : ''}£{diff.toFixed(2)}
                        </span>
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-neutral-200 font-medium text-neutral-900">
                <td className="pt-2" colSpan={2}>Total</td>
                <td className="pt-2 text-right tabular-nums">£{oldTotal.toFixed(2)}</td>
                <td className="pt-2 text-right tabular-nums">£{newTotal.toFixed(2)}</td>
                <td className="pt-2 text-right tabular-nums">
                  <span className={totalDiff > 0 ? 'text-red-600' : totalDiff < 0 ? 'text-green-600' : ''}>
                    {totalDiff > 0 ? '+' : ''}£{totalDiff.toFixed(2)}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onCancel()}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogCancel onClick={() => onConfirm(false)}>
            Keep Old Prices
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirm(true)}>
            Update & Pack
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
